import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { searchAll } from '@/lib/api';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import {
  selectAllBrands,
  selectAllFlavors,
  selectAllAttributes,
  selectGlobalDataLoading,
} from '@/store/slices/globalDataSlice';
import {
  setQuery,
  setBrandSize,
  setSelectedBrands,
  setSelectedFlavors,
  setSelectedAttributes,
  setLocationTypes,
  setVisited,
  setMinRating,
  setMaxRating,
  setDateRange,
  resetAllFilters as resetAllMapUiFilters,
  selectMapQuery,
  selectMapBrandSize,
  selectMapSelectedBrands,
  selectMapSelectedFlavors,
  selectMapSelectedAttributes,
  selectMapLocationTypes,
  selectMapVisited,
  selectMapMinRating,
  selectMapMaxRating,
  selectMapDateRange,
  selectMapIsExpanded,
  setAlgoliaSearchResults,
  setSearchModeActive,
} from '@/store/slices/mapUiSlice';
import { selectAllLocations as selectAllFirestoreLocations } from '@/store/slices/locationsSlice';
import type { SearchFilters, AlgoliaLocation, Brand, Flavor, Attribute, LocationType } from '@/types/firestore';
import { ChevronDown, X, Search, AlertCircle, Eraser } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { format, parseISO } from 'date-fns';
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import { MultiSelect } from '@/components/ui/multi-select';
import { GroupedMultiSelect } from '@/components/ui/grouped-multi-select';

// Define location type options
const locationTypes: { value: LocationType; label: string }[] = [
  { value: 'shop', label: 'Shops' },
  { value: 'brewery', label: 'Breweries' },
  { value: 'restaurant', label: 'Restaurants' },
  { value: 'other', label: 'Other' },
];

// Define the specific type for brandSize
type BrandSizeType = 'all' | 'craft' | 'mass' | 'unknown';

// Define brand size options
const brandSizeOptions: { value: BrandSizeType; label: string }[] = [
  { value: 'all', label: 'All Brands' },
  { value: 'craft', label: 'Craft Brands' },
  { value: 'mass', label: 'Mass Market' },
  { value: 'unknown', label: 'Unknown' },
];

interface FilterBadge {
  id: string;
  type: 'brand' | 'flavor' | 'attribute' | 'locationType' | 'visited' | 'rating' | 'date' | 'brandSize';
  label: string;
  value: string | number | boolean | null;
}

// Define structure for discrepancy data
interface DiscrepancyData {
  count: number;
  totalSearched: number;
  items: {
    id: string;
    name: string;
    type?: string;
    _geoloc?: { lat: number; lng: number };
  }[];
}

const FilterBox = () => {
  const dispatch = useAppDispatch();

  // Get global data from Redux store
  const brands = useAppSelector(selectAllBrands);
  const flavors = useAppSelector(selectAllFlavors);
  const attributes = useAppSelector(selectAllAttributes);
  const globalDataLoading = useAppSelector(selectGlobalDataLoading);
  const allFirestoreLocations = useAppSelector(selectAllFirestoreLocations);
  
  // Use filter state from mapUiSlice, selecting individual properties
  const mapQuery = useAppSelector(selectMapQuery);
  const mapBrandSize = useAppSelector(selectMapBrandSize);
  const mapSelectedBrands = useAppSelector(selectMapSelectedBrands);
  const mapSelectedFlavors = useAppSelector(selectMapSelectedFlavors);
  const mapSelectedAttributes = useAppSelector(selectMapSelectedAttributes);
  const mapLocationTypes = useAppSelector(selectMapLocationTypes);
  const mapVisited = useAppSelector(selectMapVisited);
  const mapMinRating = useAppSelector(selectMapMinRating);
  const mapMaxRating = useAppSelector(selectMapMaxRating);
  const mapDateRange = useAppSelector(selectMapDateRange);
  const mapIsExpanded = useAppSelector(selectMapIsExpanded);

  // Prepare location type items for MultiSelect
  const locationTypeMultiSelectItems = useMemo(() => 
    locationTypes.map(lt => ({ id: lt.value, name: lt.label })), []
  );

  // Convert mapLocationTypes to string array for the MultiSelect component
  const selectedLocationTypeIds = useMemo(() => 
    (mapLocationTypes || []) as string[], 
    [mapLocationTypes]
  );

  // Local state for controlled inputs (initialize from extended state)
  const [localSearchTerm, setLocalSearchTerm] = useState(mapQuery || '');
  const [localMinRating, setLocalMinRating] = useState<string>(mapMinRating !== null ? String(mapMinRating) : '');
  const [localMaxRating, setLocalMaxRating] = useState<string>(mapMaxRating !== null ? String(mapMaxRating) : '');

  // Sync local state with store on initial load and external changes
  useEffect(() => setLocalSearchTerm(mapQuery || ''), [mapQuery]);
  useEffect(() => setLocalMinRating(mapMinRating !== null ? String(mapMinRating) : ''), [mapMinRating]);
  useEffect(() => setLocalMaxRating(mapMaxRating !== null ? String(mapMaxRating) : ''), [mapMaxRating]);
  
  // Debounce search term update using extended state
  useEffect(() => {
    const handler = setTimeout(() => {
      if (localSearchTerm !== mapQuery) {
        dispatch(setQuery(localSearchTerm));
      }
    }, 300); // 300ms debounce

    return () => {
      clearTimeout(handler);
    };
  }, [localSearchTerm, mapQuery, dispatch]);

  const handleClearSearch = (): void => {
      setLocalSearchTerm('');
      dispatch(setQuery(''));
      dispatch(setAlgoliaSearchResults(null));
      dispatch(setSearchModeActive(false));
  };
  
  // Re-add state declarations
  const [searchLoading, setSearchLoading] = useState(false);
  const [activeFilterBadges, setActiveFilterBadges] = useState<FilterBadge[]>([]);

  const [discrepancyInfo, setDiscrepancyInfo] = useState<DiscrepancyData | null>(null);
  const [isDiscrepancyDialogOpen, setIsDiscrepancyDialogOpen] = useState(false);

  // Memoize stringified versions of arrays/objects for stable dependencies
  const stringifiedSelectedBrands = JSON.stringify(mapSelectedBrands);
  const stringifiedSelectedFlavors = JSON.stringify(mapSelectedFlavors);
  const stringifiedSelectedAttributes = JSON.stringify(mapSelectedAttributes);
  const stringifiedStoreLocationTypes = JSON.stringify(locationTypes);
  const stringifiedDateRange = JSON.stringify(mapDateRange);
  
  // Effect to rebuild filter badges when filters change
  useEffect(() => {
    const newBadges: FilterBadge[] = [];
    const currentSelectedTypes = mapLocationTypes || []; 
    
    // Add brand size badge if not "all"
    if (mapBrandSize !== 'all') {
      const option = brandSizeOptions.find(opt => opt.value === mapBrandSize);
      if (option) {
        newBadges.push({
          id: `brandSize-${mapBrandSize}`,
          type: 'brandSize',
          label: option.label,
          value: mapBrandSize
        });
      }
    }
    
    // Add brand badges (use actual brands data)
    mapSelectedBrands.forEach(brandId => {
      const brand = (brands || []).find((b: Brand) => b.id === brandId);
      if (brand) {
        newBadges.push({
          id: `brand-${brandId}`,
          type: 'brand',
          label: brand.name,
          value: brandId
        });
      }
    });
    
    // Add flavor badges (use actual flavors data)
    mapSelectedFlavors.forEach(flavorId => {
      const flavor = (flavors || []).find((f: Flavor) => f.id === flavorId);
      if (flavor) {
        newBadges.push({
          id: `flavor-${flavorId}`,
          type: 'flavor',
          label: flavor.name,
          value: flavorId
        });
      }
    });
    
    // Add attribute badges (use actual attributes data)
    mapSelectedAttributes.forEach(attrId => {
      const attr = (attributes || []).find((a: Attribute) => a.id === attrId);
      if (attr) {
        newBadges.push({
          id: `attr-${attrId}`,
          type: 'attribute',
          label: attr.name,
          value: attrId
        });
      }
    });
    
    // Add location type badges
    currentSelectedTypes.forEach((typeValue: LocationType) => {
      const locType = locationTypes.find(t => t.value === typeValue);
      if (locType) {
        newBadges.push({
          id: `locType-${typeValue}`,
          type: 'locationType',
          label: locType.label,
          value: typeValue
        });
      }
    });
    
    // Add visited badge if active
    if (mapVisited) { 
      newBadges.push({
        id: 'visited',
        type: 'visited',
        label: 'Visited Places Only',
        value: true
      });
    }

    // Add rating badge (use storeMinRating/storeMaxRating from base filters)
    const ratingLabel = mapMaxRating !== null || mapMinRating !== null ? (
      (mapMinRating !== null ? `Min ≥ ${mapMinRating.toFixed(1)}` : '') +
      (mapMinRating !== null && mapMaxRating !== null ? ' ' : '') + // Add space if both exist
      (mapMaxRating !== null ? ` Max ≤ ${mapMaxRating.toFixed(1)}` : '')
    ) : null;
    
    if (ratingLabel) {
      // Correct type for badge value - Store as string
      const ratingValue = `${mapMinRating ?? ''}-${mapMaxRating ?? ''}`;
    
      newBadges.push({
        id: 'rating',
        type: 'rating',
        label: ratingLabel.trim(), // Trim potential leading/trailing space
        value: ratingValue // Store as string
      });
    }

    // Add date range badge (requires parsing storeDateRange strings)
    const startDate = mapDateRange.start ? parseISO(mapDateRange.start) : null;
    const endDate = mapDateRange.end ? parseISO(mapDateRange.end) : null;
    if (startDate || endDate) {
        const dateLabel = (startDate ? `From ${format(startDate, 'PP')}` : '') + 
                          (startDate && endDate ? ' ' : '') + 
                          (endDate ? `To ${format(endDate, 'PP')}` : '');
        newBadges.push({
            id: 'date',
            type: 'date',
            label: dateLabel.trim(),
            value: `${mapDateRange.start || ''}|${mapDateRange.end || ''}` // Store as combined string
        });
    }
    
    setActiveFilterBadges(newBadges);
  }, [
    mapBrandSize, 
    stringifiedSelectedBrands, 
    stringifiedSelectedFlavors, 
    stringifiedSelectedAttributes, 
    mapVisited,
    mapMinRating,
    mapMaxRating,
    mapLocationTypes,
    stringifiedStoreLocationTypes,
    stringifiedDateRange,
    brands,
    flavors,
    attributes
  ]);
  
  const handleBrandSelect = (brandId: string) => {
    const currentSelection = mapSelectedBrands || [];
    const isSelected = currentSelection.includes(brandId);
    const newSelection = isSelected
        ? currentSelection.filter(id => id !== brandId)
        : [...currentSelection, brandId];
    dispatch(setSelectedBrands(newSelection));
  };

  const handleFlavorToggle = (flavorId: string) => {
    const currentSelection = mapSelectedFlavors || [];
    const isSelected = currentSelection.includes(flavorId);
    const newSelection = isSelected
        ? currentSelection.filter(id => id !== flavorId)
        : [...currentSelection, flavorId];
    dispatch(setSelectedFlavors(newSelection));
  };

  const handleAttributeToggle = (attributeId: string) => {
    const currentSelection = mapSelectedAttributes || [];
    const isSelected = currentSelection.includes(attributeId);
    const newSelection = isSelected
        ? currentSelection.filter(id => id !== attributeId)
        : [...currentSelection, attributeId];
    dispatch(setSelectedAttributes(newSelection));
  };

  const handleLocationTypeToggle = (locationTypeId: string) => {
    const currentSelectedTypes = mapLocationTypes || [];
    const isSelected = currentSelectedTypes.includes(locationTypeId as LocationType);
    const newTypes = isSelected
      ? currentSelectedTypes.filter(t => t !== locationTypeId)
      : [...currentSelectedTypes, locationTypeId as LocationType];
    
    dispatch(setLocationTypes(newTypes.length > 0 ? newTypes : null));
  };

  const handleRemoveBadge = (badge: FilterBadge) => {
    switch (badge.type) {
      case 'brandSize':
        dispatch(setBrandSize('all'));
        break;
      case 'brand':
        dispatch(setSelectedBrands(mapSelectedBrands.filter(id => id !== badge.value)));
        break;
      case 'flavor':
        dispatch(setSelectedFlavors(mapSelectedFlavors.filter(id => id !== badge.value)));
        break;
      case 'attribute':
        dispatch(setSelectedAttributes(mapSelectedAttributes.filter(id => id !== badge.value)));
        break;
      case 'locationType':
        const currentSelectedTypes = mapLocationTypes || [];
        dispatch(setLocationTypes(currentSelectedTypes.filter((t: LocationType) => t !== badge.value)));
        break;
      case 'visited':
        dispatch(setVisited(null));
        break;
      case 'rating':
        dispatch(setMinRating(null));
        dispatch(setMaxRating(null));
        break;
      case 'date':
        dispatch(setDateRange({ start: null, end: null }));
        break;
    }
  };
  
  // Reset local state and store
  const resetAllFilters = () => {
    dispatch(resetAllMapUiFilters());
    setLocalSearchTerm('');
    setLocalMinRating('');
    setLocalMaxRating('');
  };

  // Function to perform the search
  const handleSearch = async (): Promise<void> => {
    setSearchLoading(true);
    setDiscrepancyInfo(null);
    setIsDiscrepancyDialogOpen(false);

    try {
      // Build the search filters using state from extendedFilters primarily
      const filters: Partial<SearchFilters> & { hitsPerPage?: number } = {
        query: mapQuery.trim() || undefined, 
        brandSize: mapBrandSize !== 'all' ? mapBrandSize as BrandSizeType : undefined,
        brandIds: mapSelectedBrands.length > 0 ? mapSelectedBrands : undefined, 
        flavorIds: mapSelectedFlavors.length > 0 ? mapSelectedFlavors : undefined, 
        attributeIds: mapSelectedAttributes.length > 0 ? mapSelectedAttributes : undefined, 
        minRating: mapMinRating,
        maxRating: mapMaxRating,
        locationType: mapLocationTypes?.length ? mapLocationTypes.join(',') : undefined,
        startDate: mapDateRange.start, 
        endDate: mapDateRange.end, 
        hitsPerPage: 1000,
      };
      
      // Clean undefined values before sending
      Object.keys(filters).forEach(key => filters[key as keyof typeof filters] === undefined && delete filters[key as keyof typeof filters]);

      console.log("Sending search filters:", filters);

      // Pass query separately from the filters object if needed by API, otherwise keep in filters
      const searchResults = await searchAll(filters.query, filters);

      let resultLocations: AlgoliaLocation[] | null = null;
      if (searchResults.locations && Array.isArray(searchResults.locations.hits)) {
        resultLocations = searchResults.locations.hits; 
      } else if (searchResults.drinks && Array.isArray(searchResults.drinks.hits) && (!searchResults.locations || !searchResults.locations.hits)) {
        console.warn("Search returned drinks but no locations matching the criteria for map display.");
        resultLocations = [];
      } else {
        resultLocations = [];
      }
      
      // Dispatch actions to store Algolia results and set search mode active
      dispatch(setAlgoliaSearchResults(resultLocations));
      dispatch(setSearchModeActive(true));
      
      // --- Discrepancy Check ---
      if (resultLocations && allFirestoreLocations) {
        const searchResultIds = new Set(resultLocations.map(loc => loc.objectID));
        const firestoreLocationIds = new Set(allFirestoreLocations.map(loc => loc.id));
        const discrepancies: DiscrepancyData['items'] = [];

        searchResultIds.forEach(searchId => {
          if (!firestoreLocationIds.has(searchId)) {
            const discrepantLoc = resultLocations?.find(loc => loc.objectID === searchId);
            discrepancies.push({
              id: searchId,
              name: discrepantLoc ? discrepantLoc.name : 'Unknown Name',
              type: discrepantLoc?.locationType,
              _geoloc: discrepantLoc?._geoloc
            });
          }
        });

        if (discrepancies.length > 0) {
          const totalSearchedCount = resultLocations.length;
          console.warn(`Discrepancy found: ${discrepancies.length} out of ${totalSearchedCount} locations from search results not in Firestore store.`, discrepancies.map(d => d.id));
          const data: DiscrepancyData = {
            count: discrepancies.length,
            totalSearched: totalSearchedCount,
            items: discrepancies
          };
          setDiscrepancyInfo(data);
          setIsDiscrepancyDialogOpen(true);
        }
      } else {
         setDiscrepancyInfo(null);
      }
      // --- End Discrepancy Check ---

    } catch (error) {
      console.error('Search failed:', error);
      setDiscrepancyInfo(null);
    } finally {
      setSearchLoading(false);
    }
  };
  
  // Render Loading state for initial data fetch
  if (globalDataLoading === 'pending') {
    return (
       <Card className="w-full shadow-sm border-border bg-card/90 backdrop-blur-sm p-4">
         <CardContent className="flex items-center justify-center">
           <LoadingSpinner /> 
           <span className="ml-2">Loading filters...</span>
         </CardContent>
       </Card>
    );
  }

  return (
    <>
          {mapIsExpanded && (
            <div className="space-y-3 mt-3">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Freetext filter..."
                  value={localSearchTerm}
                  onChange={(e) => setLocalSearchTerm(e.target.value)}
                  className="w-full pl-8"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSearch();
                    }
                  }}
                />
                {localSearchTerm && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 p-0"
                    onClick={handleClearSearch}
                    aria-label="Clear search"
                  >
                    <X size={14} />
                  </Button>
                )}
              </div>

              <div>
                <Popover>
                  <PopoverTrigger asChild>
                    <div className="flex h-9 w-full cursor-pointer items-center justify-between rounded-md border border-input bg-transparent px-3 py-2 text-sm ring-offset-background focus-within:outline-none focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
                      <span className={mapBrandSize === 'all' ? 'text-muted-foreground' : 'text-card-foreground'}>
                        {mapBrandSize !== 'all' 
                          ? brandSizeOptions.find(opt => opt.value === mapBrandSize)?.label
                          : 'Select brand size...'}
                      </span>
                      <ChevronDown size={14} className="ml-2 shrink-0 opacity-50" />
                    </div>
                  </PopoverTrigger>
                  <PopoverContent className="w-[200px] p-2">
                    <RadioGroup 
                      value={mapBrandSize} 
                      onValueChange={(value: string) => dispatch(setBrandSize(value as BrandSizeType))}
                      className="gap-2"
                    >
                      {brandSizeOptions.map((option) => (
                        <div key={option.value} className="flex items-center space-x-2">
                          <RadioGroupItem 
                            id={`brandsize-${option.value}`} 
                            value={option.value} 
                          />
                          <Label 
                            htmlFor={`brandsize-${option.value}`}
                            className="text-sm cursor-pointer"
                          >
                            {option.label}
                          </Label>
                        </div>
                      ))}
                    </RadioGroup>
                  </PopoverContent>
                </Popover>
              </div>

              <div>
                <MultiSelect
                  items={brands || []}
                  selectedIds={mapSelectedBrands}
                  onToggleItem={handleBrandSelect}
                  placeholder="Select brands..."
                  emptyMessage="No brands found"
                  searchPlaceholder="Search brands..."
                  disabled={(brands || []).length === 0}
                />
              </div>

              <div>
                <GroupedMultiSelect
                  items={flavors || []}
                  selectedIds={mapSelectedFlavors}
                  onToggleItem={handleFlavorToggle}
                  placeholder="Select flavors..."
                  emptyMessage="No flavors found"
                  searchPlaceholder="Search flavors..."
                  disabled={(flavors || []).length === 0}
                />
              </div>

              <div>
                <GroupedMultiSelect
                  items={attributes || []}
                  selectedIds={mapSelectedAttributes}
                  onToggleItem={handleAttributeToggle}
                  placeholder="Select attributes..."
                  emptyMessage="No attributes found"
                  searchPlaceholder="Search attributes..."
                  disabled={(attributes || []).length === 0}
                />
              </div>

              <div>
                <MultiSelect
                  items={locationTypeMultiSelectItems}
                  selectedIds={selectedLocationTypeIds}
                  onToggleItem={handleLocationTypeToggle}
                  placeholder="Select location types..."
                  emptyMessage="No location types found"
                  searchPlaceholder="Search location types..."
                />
              </div>
              
              <div>
                <div className="flex items-center gap-2">
                  <Input
                    id="minRating"
                    type="text"
                    inputMode="decimal"
                    min={0}
                    max={5}
                    step={0.1}
                    value={localMinRating}
                    onChange={(e) => {
                      const inputValue = e.target.value;
                      if (/^\d*[,\.]?\d*$/.test(inputValue)) {
                        setLocalMinRating(inputValue);

                        const normalizedValue = inputValue.replace(',', '.');

                        if (normalizedValue === '' || normalizedValue === '.') {
                          dispatch(setMinRating(null));
                        } else {
                          const numericValue = parseFloat(normalizedValue);
                          if (!isNaN(numericValue) && numericValue >= 0 && numericValue <= 5) {
                            dispatch(setMinRating(numericValue));
                          } else {
                            dispatch(setMinRating(null));
                          }
                        }
                      }
                    }}
                    className="w-full"
                    placeholder="Min rating"
                  />
                  <Input
                    id="maxRating"
                    type="text"
                    inputMode="decimal"
                    min={0}
                    max={5}
                    step={0.1}
                    value={localMaxRating}
                    onChange={(e) => {
                      const inputValue = e.target.value;
                      if (/^\d*[,\.]?\d*$/.test(inputValue)) {
                        setLocalMaxRating(inputValue);

                        const normalizedValue = inputValue.replace(',', '.');

                        if (normalizedValue === '' || normalizedValue === '.') {
                          dispatch(setMaxRating(null));
                        } else {
                          const numericValue = parseFloat(normalizedValue);
                          if (!isNaN(numericValue) && numericValue >= 0 && numericValue <= 5) {
                            dispatch(setMaxRating(numericValue));
                          } else {
                            dispatch(setMaxRating(null));
                          }
                        }
                      }
                    }}
                    className="w-full"
                    placeholder="Max rating"
                  />
                </div>
              </div>

              <div className="pt-2 space-y-2">
                <div className="flex items-center justify-between">
                   <Label htmlFor="visited" className="text-sm cursor-pointer flex-grow mr-2">Only visited places</Label>
                  <Switch 
                    id="visited" 
                    checked={mapVisited === true}
                    onCheckedChange={(checked) => dispatch(setVisited(checked ? true : null))}
                  />
                </div>
              </div>
              
              {activeFilterBadges.length > 0 && (
                <div className="flex flex-wrap gap-1 pt-3">
                  {activeFilterBadges.map((badge) => (
                    <Badge 
                      key={badge.id} 
                      variant="secondary" 
                      className="group relative px-2 py-1 text-xs"
                    >
                      {badge.label}
                      <button 
                        onClick={() => handleRemoveBadge(badge)}
                         className="ml-1 -mr-1 p-0.5 rounded-full opacity-50 group-hover:opacity-100 hover:bg-muted transition-opacity"
                        aria-label={`Remove ${badge.label} filter`}
                      >
                        <X size={10} />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}

              <div className="flex gap-2 items-center mt-4">
                  <Button 
                    onClick={handleSearch} 
                    disabled={searchLoading}
                    className="flex-grow"
                  >
                    {searchLoading ? <><LoadingSpinner className="mr-2 h-4 w-4 animate-spin" />Searching...</> : 'Search'}
                  </Button>
                  {activeFilterBadges.length > 0 && !searchLoading && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={resetAllFilters}
                      className="text-muted-foreground"
                      disabled={searchLoading}
                      aria-label="Reset all filters"
                    >
                      <Eraser size={14} className="mr-1" /> Reset
                    </Button>
                  )}
               </div>
            </div>
          )}

      {/* Discrepancy Dialog */}
      <Dialog open={isDiscrepancyDialogOpen} onOpenChange={setIsDiscrepancyDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <AlertCircle className="h-5 w-5 mr-2 text-destructive" />
              Data Discrepancy Detected
            </DialogTitle>
            <DialogDescription>
              {discrepancyInfo?.count} out of {discrepancyInfo?.totalSearched} location(s) from search results are not present in the current map data (Firestore). This might indicate outdated search index data.
            </DialogDescription>
          </DialogHeader>
          {discrepancyInfo && discrepancyInfo.items.length > 0 && (
            <div className="mt-4 max-h-80 overflow-y-auto pr-2">
              <p className="font-medium mb-2">Discrepant Locations:</p>
              <ul className="space-y-3 text-sm">
                {discrepancyInfo.items.map((item) => (
                  <li key={item.id} className="border-b border-border pb-2 last:border-b-0 last:pb-0">
                    <p className="font-semibold">{item.name}</p>
                    <p className="text-xs text-muted-foreground break-all">ID: {item.id}</p>
                    {item.type && (
                      <p className="text-xs text-muted-foreground">Type: {item.type}</p>
                    )}
                    {item._geoloc && (
                      <p className="text-xs text-muted-foreground">
                        Coords: {item._geoloc.lat.toFixed(6)}, {item._geoloc.lng.toFixed(6)}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <DialogFooter className="mt-4">
            <DialogClose asChild>
              <Button type="button" variant="secondary">
                Close
              </Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FilterBox; 